/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
/* eslint-disable @atlassian/perf-linting/no-chain-state-updates */
/* eslint-disable @atlassian/perf-linting/no-expensive-computations-in-render */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import UndoIcon from '@atlaskit/icon/core/undo';
import Heading from '@atlaskit/heading';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Anchor, Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import { ModalTransition } from '@atlaskit/modal-dialog';
import FolderOpenIcon from '@atlaskit/icon/core/folder-open';
import PageIcon from '@atlaskit/icon/core/page';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import Checkbox from '@atlaskit/checkbox';
import Spinner from '@atlaskit/spinner';

import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { expVal } from '@confluence/feature-experiments';

import { SuggestionMovePopup } from './SuggestionMovePopup.tsx';
import { useContentTitles } from './hooks/useContentTitles.ts';
import { useSubmitSuggestionActions } from './hooks/useSubmitSuggestionActions.ts';
import {
	getSuggestionIconUrlFromContentUrl,
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
	type SuggestionGroup,
} from './__types__/RemoteObject.ts';
import { SuggestionHoverCard } from './SuggestionHoverCard.tsx';
import { ContentPreviewPlaceholder } from './ContentPreviewPlaceholder.tsx';
import { useCreatorInfo } from './hooks/useCreatorInfo.ts';
import placeholderIcon from './img/content-preview-placeholder-icon.svg';
import { SuggestionFeedbackModal } from './SuggestionFeedbackModal.tsx';
import googleDriveIcon from './img/google-drive.svg';
import loomIcon from './img/loom.svg';
import connectArrowIcon from './img/connect-arrow.png';

import previewBackground from './img/preview-background.svg';
import sparkleIcon from './img/content-preview-sparkle.svg';

type PreviewIconSource = 'google-drive' | 'loom' | 'microsoft-teams';

const PRODUCT_DISPLAY_NAMES: Record<PreviewIconSource, string> = {
	'google-drive': 'Google Drive',
	loom: 'Loom',
	'microsoft-teams': 'Microsoft Teams',
};

const getPreviewSourceFromProductKey = (productKey: string): PreviewIconSource | undefined => {
	switch (productKey) {
		case 'googleDocs':
		case 'googleSheets':
		case 'googleSlides':
		case 'googleDrive':
			return 'google-drive';
		case 'loom':
			return 'loom';
		case 'microsoftTeams':
			return 'microsoft-teams';
		default:
			return undefined;
	}
};

const SUGGESTION_PREVIEW_DWELL_MS = 500;

function useSuggestionPreviewAnalytics({
	spaceId,
	spaceKey,
	suggestion,
}: {
	spaceId?: string;
	spaceKey?: string;
	suggestion: Suggestion;
}) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hoverStartRef = useRef<number | null>(null);
	const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (previewTimeoutRef.current) {
				clearTimeout(previewTimeoutRef.current);
			}
		};
	}, []);

	const startPreviewAnalytics = useCallback(() => {
		hoverStartRef.current = Date.now();
		previewTimeoutRef.current = setTimeout(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'previewed',
					actionSubject: 'suggestion',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						suggestionId: suggestion.id,
						suggestionUrl: suggestion.url,
						suggestedParentId: suggestion.parentId,
						sourceType: getSuggestionProductKeyFromContentUrl(suggestion.url),
						previewDwellMs: Date.now() - (hoverStartRef.current ?? Date.now()),
					},
				},
			}).fire();
			previewTimeoutRef.current = null;
		}, SUGGESTION_PREVIEW_DWELL_MS);
	}, [createAnalyticsEvent, spaceId, spaceKey, suggestion]);

	const cancelPreviewAnalytics = useCallback(() => {
		if (previewTimeoutRef.current) {
			clearTimeout(previewTimeoutRef.current);
			previewTimeoutRef.current = null;
		}
		hoverStartRef.current = null;
	}, []);

	return { startPreviewAnalytics, cancelPreviewAnalytics };
}

const i18n = defineMessages({
	addItemsToSpace: {
		id: 'space-curation.review-content-suggestions-modal.add-items-to-space',
		defaultMessage: 'Add {count, plural, one {# item} other {# items}} to space',
		description: 'Button label for adding selected content suggestions to space',
	},
	bringTogetherTitle: {
		id: 'space-curation.review-content-suggestions-modal.bring-together-title',
		defaultMessage: 'Bring together the content that matters',
		description: 'Title for the review content suggestions modal',
	},
	bringTogetherDescription: {
		id: 'space-curation.review-content-suggestions-modal.bring-together-description',
		defaultMessage: "Add these suggestions and they'll be organized in this space.",
		description: 'Description for the review content suggestions modal',
	},
	openSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.open-suggestion',
		defaultMessage: 'Open {title}',
		description: 'Aria label for opening a suggestion',
	},
	feedbackButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.feedback-button-label',
		defaultMessage: 'Give feedback on {title}',
		description: 'Aria label for the feedback button on a suggestion',
	},
	evalButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.eval-button-label',
		defaultMessage: 'Eval',
		description: 'Label for the eval button on a suggestion',
	},
	untitled: {
		id: 'space-curation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback title for a suggestion without a title',
	},
	loading: {
		id: 'space-curation.review-content-suggestions-modal.loading',
		defaultMessage: 'Loading',
		description: 'Loading state label for the add button',
	},
	submitting: {
		id: 'space-curation.review-content-suggestions-modal.submitting',
		defaultMessage: 'Adding...',
		description: 'Submitting state label for the add button',
	},
	rejectSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.reject-suggestion',
		defaultMessage: 'Reject {title}',
		description: 'Aria label for the thumbs down button to reject a suggestion',
	},
	resetSuggestion: {
		id: 'space-curation.review-content-suggestions-modal.reset-suggestion',
		defaultMessage: 'Restore suggestion',
		description: 'Aria label for the undo button to restore a rejected suggestion',
	},
	addToThisSpace: {
		id: 'space-curation.review-content-suggestions-modal.add-to-this-space',
		defaultMessage: 'Add to this Space',
		description: 'Disabled button label when no items are selected or thumbed down',
	},
	dismissItems: {
		id: 'space-curation.review-content-suggestions-modal.dismiss-items',
		defaultMessage: 'Dismiss Items',
		description: 'Warning button label when some items are thumbed down but none are selected',
	},
});

const styles = cssMap({
	layout: {
		width: '1152px',
		height: '665px',
		borderRadius: token('radius.xlarge'),
		overflow: 'hidden',
	},
	leftPanel: {
		flexShrink: 0,
		flexBasis: '624px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
		paddingInline: token('space.600'),
		overflow: 'hidden',
	},
	rightPanel: {
		flexGrow: 1,
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
		position: 'relative',
		borderStartEndRadius: token('radius.xlarge'),
		borderEndEndRadius: token('radius.xlarge'),
	},
	closeButtonWrapper: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 800,
	},
	groupListScrollArea: {
		flexGrow: 1,
		overflowY: 'auto',
		scrollbarWidth: 'none',
		minHeight: '0%',
	},
	groupCard: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	groupCardHovering: {
		position: 'relative',
		zIndex: 700,
	},
	groupRow: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		gap: token('space.100'),
	},
	groupIconSlot: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	groupTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	itemsList: {
		overflow: 'visible',
		minHeight: '0%',
		paddingInline: token('space.100'),
		paddingBlockEnd: token('space.100'),
	},
	itemRowContainer: {
		position: 'relative',
		zIndex: 0,
		width: '100%',
		transition: 'background-color 150ms ease',
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	itemRowContainerHovered: {
		zIndex: 1,
	},
	itemRowContainerCardHovered: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
	portalHoverCard: {
		position: 'fixed',
		zIndex: 800,
		pointerEvents: 'none',
		opacity: 0,
		transform: 'scale(0.97)',
		transition: 'opacity 120ms ease-out, transform 120ms ease-out',
	},
	portalHoverCardVisible: {
		opacity: 1,
		pointerEvents: 'auto',
		transform: 'scale(1)',
	},
	suggestionLinkWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
	},
	suggestionLink: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		minWidth: '0%',
		textDecoration: 'none',
		color: token('color.text'),
		flexGrow: 1,
		'&:hover': {
			textDecoration: 'underline',
			textDecorationColor: token('color.text'),
		},
	},
	suggestionIconSlot: {
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral'),
	},
	suggestionTitleWrapper: {
		flexGrow: 1,
		minWidth: '0%',
		overflow: 'hidden',
	},
	leftPanelHeader: {},
	contentPreviewContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		position: 'relative',
		overflow: 'hidden',
	},
	contentPreviewUnit: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '580px',
		height: '520px',
	},
	contentPreviewDecorativeBackground: {
		position: 'absolute',
		// @ts-expect-error
		top: '12px',
		// @ts-expect-error
		left: '-28px',
	},
	contentPreviewIllustration: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	contentPreviewIconTopLeft: {
		position: 'absolute',
		// @ts-expect-error
		top: '30px',
		// @ts-expect-error
		left: '-54px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.100'),
		// @ts-expect-error
		zIndex: 'modal',
	},
	contentPreviewIconBottomRight: {
		position: 'absolute',
		// @ts-expect-error
		bottom: '40px',
		// @ts-expect-error
		right: '-46px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		// @ts-expect-error
		zIndex: 'modal',
	},
	contentPreviewSparkle: {
		position: 'absolute',
		// @ts-expect-error
		top: '-54px',
		// @ts-expect-error
		right: '-46px',
		transform: 'rotate(-8.497deg)',
		// @ts-expect-error
		zIndex: 'modal',
	},
	contentPreviewGoogleDriveIcon: {
		transform: 'rotate(-15deg)',
		// @ts-expect-error
		border: `2px solid ${token('color.border')}`,
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		width: '36px',
		height: '36px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	contentPreviewLoomIcon: {
		transform: 'rotate(15deg)',
		// @ts-expect-error -- eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginInlineStart: '64px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
	},
	contentPreviewArrowTopLeft: {
		transform: 'scaleY(-1) rotate(150deg)',
		// @ts-expect-error -- eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginInlineEnd: '-38px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
	},
	contentPreviewDarkBlueBox: {
		position: 'absolute',
		width: '316px',
		height: '253px',
		backgroundColor: token('color.background.brand.bold'),
		transform: 'rotate(-1.25deg)',
		// @ts-expect-error
		bottom: '-10px',
		// @ts-expect-error
		left: '-10px',
	},
	contentPreviewCard: {
		display: 'flex',
		width: '328px',
		height: '368px',
		backgroundColor: token('elevation.surface'),
		position: 'relative',
		overflow: 'visible',
	},
	contentPreviewImageContainer: {
		width: '100%',
		overflow: 'hidden',
	},
	contentPreviewImageFallback: {
		width: '100%',
		height: '130px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	contentPreviewTitle: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	contentPreviewProductLabel: {
		position: 'absolute',
		bottom: token('space.300'),
		left: token('space.250'),
	},
	contentPreviewCardPadding: {
		padding: token('space.250'),
		paddingBlockEnd: token('space.300'),
		width: '100%',
	},
	addButtonRow: {},
	itemRow: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.100'),
		gap: token('space.050'),
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	unselectedLink: {
		'&:hover': {
			textDecoration: 'none',
		},
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '1152px',
		height: '665px',
	},
	feedbackIconButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	thumbsDownButton: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	itemRowContainerRejected: {
		backgroundColor: token('color.background.disabled'),
	},
});

// Estimated max height of the hover cards (in px) used for vertical clamping
const HOVER_CARD_HEIGHT = 200;
// Must match the width set in SuggestionHoverCard styles
const HOVER_CARD_WIDTH = 340;
const HOVER_CARD_OFFSET = 8;

type HoverCardPosition = { top: number; left: number };
type HoverCardPlacement = 'right-start' | 'left-start';

const computePosition = (anchorRect: DOMRect, placement: HoverCardPlacement): HoverCardPosition => {
	const top =
		window.innerHeight - anchorRect.top < HOVER_CARD_HEIGHT + HOVER_CARD_OFFSET
			? window.innerHeight - HOVER_CARD_HEIGHT - HOVER_CARD_OFFSET
			: anchorRect.top;
	const preferredLeft =
		placement === 'left-start'
			? anchorRect.left - HOVER_CARD_WIDTH - HOVER_CARD_OFFSET
			: anchorRect.right + HOVER_CARD_OFFSET;
	const left = Math.min(
		Math.max(preferredLeft, HOVER_CARD_OFFSET),
		window.innerWidth - HOVER_CARD_WIDTH - HOVER_CARD_OFFSET,
	);
	return { top, left };
};

// Delay before showing the card, preventing accidental triggers when scanning the list (matches page tree's HOVER_DELAY_TIME)
const HOVER_CARD_SHOW_DELAY_MS = 300;
// Delay before hiding the card, giving the user time to move the mouse onto it (matches page tree's CLOSE_DELAY_TIME)
const HOVER_CARD_HIDE_DELAY_MS = 400;
const ESCAPE_KEY = 'Escape';

// Module-level state for coordinating hover cards across multiple hook instances.
// When one row's card shows, any previously active card is hidden immediately so only
// one card is ever visible at a time.
let sharedHideTimer: ReturnType<typeof setTimeout> | null = null;
let sharedShowTimer: ReturnType<typeof setTimeout> | null = null;
// Callback that immediately hides the currently active card instance
let activeHideCallback: (() => void) | null = null;
// Tracks which hook instance owns the pending show timer so unmount cleanup
// doesn't cancel a different instance's timer (real bug: unrelated unmount
// would silently suppress another row's hover card from appearing)
let sharedShowTimerOwner: symbol | null = null;

const clearSharedHideTimer = () => {
	if (sharedHideTimer !== null) {
		clearTimeout(sharedHideTimer);
		sharedHideTimer = null;
	}
};

const clearSharedShowTimer = () => {
	if (sharedShowTimer !== null) {
		clearTimeout(sharedShowTimer);
		sharedShowTimer = null;
		sharedShowTimerOwner = null;
	}
};

// Exported for tests to reset module-level state between test cases
export const resetSharedHoverState = () => {
	clearSharedHideTimer();
	clearSharedShowTimer();
	activeHideCallback = null;
	sharedShowTimerOwner = null;
};

const useHoverCardPortal = (placement: HoverCardPlacement = 'right-start') => {
	const ref = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [position, setPosition] = useState<HoverCardPosition | null>(null);
	// Tracks whether the mouse is hovering the card portal so the anchor row can be highlighted
	const [isCardHovered, setIsCardHovered] = useState(false);

	// Immediate (non-delayed) hide used when another instance takes over
	const forceHide = useCallback(() => {
		setIsVisible(false);
		setPosition(null);
		setIsCardHovered(false);
	}, []);

	// Compute and update position from the anchor's current bounding rect
	const updatePosition = useCallback(() => {
		if (ref.current) {
			setPosition(computePosition(ref.current.getBoundingClientRect(), placement));
		}
	}, [placement]);

	// Recompute position once the card becomes visible (anchor may have moved during show delay)
	useEffect(() => {
		if (isVisible) {
			updatePosition();
		}
	}, [isVisible, updatePosition]);

	const instanceId = useRef(Symbol('hover-card-instance'));

	const show = useCallback(() => {
		// Cancel any pending delayed hide or show
		clearSharedHideTimer();
		clearSharedShowTimer();
		// Force-hide the currently active card from a different instance
		if (activeHideCallback && activeHideCallback !== forceHide) {
			activeHideCallback();
		}
		// Delay showing so quick mouse scans don't trigger cards
		sharedShowTimerOwner = instanceId.current;
		sharedShowTimer = setTimeout(() => {
			sharedShowTimer = null;
			sharedShowTimerOwner = null;
			activeHideCallback = forceHide;
			setIsVisible(true);
		}, HOVER_CARD_SHOW_DELAY_MS);
	}, [forceHide]);

	const hide = useCallback(() => {
		// Cancel any pending show timer (mouse left before delay elapsed)
		clearSharedShowTimer();
		// Delay hide so mouse can travel from anchor onto the card without it disappearing
		sharedHideTimer = setTimeout(() => {
			sharedHideTimer = null;
			if (activeHideCallback === forceHide) {
				activeHideCallback = null;
			}
			forceHide();
		}, HOVER_CARD_HIDE_DELAY_MS);
	}, [forceHide]);

	// Cancel the delayed hide when mouse enters the card itself
	const onCardMouseEnter = useCallback(() => {
		clearSharedHideTimer();
		setIsCardHovered(true);
	}, []);

	// Hide when mouse leaves the card
	const onCardMouseLeave = useCallback(() => {
		setIsCardHovered(false);
		hide();
	}, [hide]);

	// Dismiss on Escape key
	useEffect(() => {
		if (!isVisible) {
			return;
		}
		const handleEscKey = (e: KeyboardEvent) => {
			if (e.key === ESCAPE_KEY) {
				clearSharedHideTimer();
				clearSharedShowTimer();
				if (activeHideCallback === forceHide) {
					activeHideCallback = null;
				}
				forceHide();
			}
		};
		document.addEventListener('keydown', handleEscKey);
		return () => {
			document.removeEventListener('keydown', handleEscKey);
		};
	}, [isVisible, forceHide]);

	// On unmount, only clean up timers/state that this instance owns
	useEffect(() => {
		// Capture the instance ID at effect creation time so the cleanup closure
		// references a stable value (direct .current access in cleanup is a lint error)
		const currentInstanceId = instanceId.current;
		return () => {
			// Only cancel the show timer if this instance scheduled it
			if (sharedShowTimerOwner === currentInstanceId) {
				clearSharedShowTimer();
			}
			if (activeHideCallback === forceHide) {
				clearSharedHideTimer();
				activeHideCallback = null;
			}
		};
	}, [forceHide]);

	return {
		ref,
		isVisible,
		isCardHovered,
		position,
		show,
		hide,
		onCardMouseEnter,
		onCardMouseLeave,
	};
};

type SuggestionItemRowProps = {
	suggestion: Suggestion;
	hasFeedbackBeenSubmitted?: boolean;
	onFeedbackSubmitted?: (suggestionId: string) => void;
	isSelected: boolean;
	isRejected: boolean;
	spaceId?: string;
	spaceKey?: string;
	onToggleSelectItem: (id: string) => void;
	onRejectItem: (id: string) => void;
	onHoverCardVisibilityChange: (isVisible: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
};

const SuggestionItemRow = ({
	spaceId,
	spaceKey,
	suggestion,
	isSelected,
	isRejected,
	hasFeedbackBeenSubmitted,
	onFeedbackSubmitted,
	onToggleSelectItem,
	onRejectItem,
	onHoverCardVisibilityChange,
	onMoveSuggestion,
	onSuggestionHover,
}: SuggestionItemRowProps) => {
	const { formatMessage } = useIntl();
	const iconUrl = getSuggestionIconUrlFromContentUrl(suggestion.url);
	const {
		ref,
		isVisible,
		isCardHovered,
		position,
		show,
		hide,
		onCardMouseEnter,
		onCardMouseLeave,
	} = useHoverCardPortal('right-start');
	// isRowHovered tracks immediate mouse-over state for row UI elements (Move button, row highlight)
	// without waiting for the card's 300ms show delay
	const [isRowHovered, setIsRowHovered] = useState(false);
	const { startPreviewAnalytics, cancelPreviewAnalytics } = useSuggestionPreviewAnalytics({
		spaceId,
		spaceKey,
		suggestion,
	});
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
	const isFeedbackModalOpenRef = useRef(false);
	const [isMovePopupOpen, setIsMovePopupOpen] = useState(false);
	const isMovePopupOpenRef = useRef(false);

	const handleShow = () => {
		if (isRejected) return;
		setIsRowHovered(true);
		onSuggestionHover?.(suggestion);
		if (!isSelected) return;
		if (isMovePopupOpenRef.current || isFeedbackModalOpenRef.current) return;
		show();
		onHoverCardVisibilityChange(true);
		startPreviewAnalytics();
	};

	const handleHide = () => {
		if (isRejected) return;
		setIsRowHovered(false);
		onSuggestionHover?.(null);
		hide();
		onHoverCardVisibilityChange(false);
		cancelPreviewAnalytics();
	};

	const handleMovePopupOpenChange = (open: boolean) => {
		isMovePopupOpenRef.current = open;
		setIsMovePopupOpen(open);
		if (open) {
			handleHide();
		}
	};

	useEffect(() => {
		if (!isSelected) {
			handleHide();
			handleMovePopupOpenChange(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelected]);

	return (
		<Box
			ref={ref}
			xcss={cx(
				styles.itemRowContainer,
				!isRejected && (isRowHovered || isCardHovered) && styles.itemRowContainerHovered,
				!isRejected && isCardHovered && styles.itemRowContainerCardHovered,
				isRejected && styles.itemRowContainerRejected,
			)}
			onMouseEnter={handleShow}
			onMouseLeave={handleHide}
			onFocusCapture={handleShow}
			onBlur={(event) => {
				if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
				handleHide();
			}}
			testId={`review-content-suggestion-row-${suggestion.id}`}
		>
			<Flex alignItems="center" xcss={styles.itemRow}>
				<Box xcss={styles.suggestionLinkWrapper}>
					<Box
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={isRejected ? { opacity: 0.5 } : undefined}
						xcss={styles.suggestionIconSlot}
						aria-hidden="true"
					>
						{iconUrl ? (
							<FallbackableImage
								src={iconUrl}
								width="20"
								height="20"
								fallback={() => <DefaultAppIcon label="" />}
								role="presentation"
							/>
						) : (
							<DefaultAppIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Anchor
						href={suggestion.url}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={formatMessage(i18n.openSuggestion, { title: suggestion.title })}
						xcss={cx(styles.suggestionLink, !isSelected && styles.unselectedLink)}
					>
						<Box xcss={styles.suggestionTitleWrapper}>
							<Text maxLines={1} color={isRejected ? 'color.text.disabled' : undefined}>
								{suggestion.title}
							</Text>
						</Box>
					</Anchor>
				</Box>
				{!hasFeedbackBeenSubmitted &&
					expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
						<Pressable
							xcss={styles.feedbackIconButton}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								isFeedbackModalOpenRef.current = true;
								handleHide();
								setIsFeedbackModalOpen(true);
							}}
							aria-label={formatMessage(i18n.feedbackButtonLabel, { title: suggestion.title })}
						>
							<Text size="small" color="color.text.subtle">
								{formatMessage(i18n.evalButtonLabel)}
							</Text>
						</Pressable>
					)}
				{!isRejected && (isRowHovered || isMovePopupOpen) && (
					<Inline space="space.050" alignBlock="center">
						<SuggestionMovePopup
							suggestionId={suggestion.id}
							spaceKey={spaceKey}
							isOpen={isMovePopupOpen}
							onOpenChange={handleMovePopupOpenChange}
							onMoveSuggestion={onMoveSuggestion}
							fallbackParents={suggestion.fallbackParents}
							currentParentId={suggestion.parentId}
						/>
						<Pressable
							xcss={styles.thumbsDownButton}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onRejectItem(suggestion.id);
							}}
							aria-label={formatMessage(i18n.rejectSuggestion, { title: suggestion.title })}
						>
							<ThumbsDownIcon label="" color={token('color.icon.subtle')} />
						</Pressable>
					</Inline>
				)}
				{isRejected && (
					<Box padding="space.050">
						<IconButton
							icon={UndoIcon}
							label={formatMessage(i18n.resetSuggestion, { title: suggestion.title })}
							appearance="subtle"
							spacing="compact"
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								onRejectItem(suggestion.id);
							}}
						/>
					</Box>
				)}
				{!isRejected && (
					<Box padding="space.050">
						<Checkbox isChecked={isSelected} onChange={() => onToggleSelectItem(suggestion.id)} />
					</Box>
				)}
			</Flex>
			<ModalTransition>
				{isFeedbackModalOpen && expVal('cc-space-curation-feedback-survey', 'isEnabled', false) && (
					<SuggestionFeedbackModal
						suggestion={suggestion}
						spaceId={spaceId}
						spaceKey={spaceKey}
						onClose={() => {
							isFeedbackModalOpenRef.current = false;
							setIsFeedbackModalOpen(false);
						}}
						onSubmit={() => onFeedbackSubmitted?.(suggestion.id)}
					/>
				)}
			</ModalTransition>
			{isVisible &&
				position &&
				createPortal(
					<Box
						xcss={cx(styles.portalHoverCard, isVisible && styles.portalHoverCardVisible)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ top: position.top, left: position.left }}
						testId="review-content-suggestion-hover-card-portal"
						onMouseEnter={onCardMouseEnter}
						onMouseLeave={onCardMouseLeave}
					>
						<SuggestionHoverCard suggestion={suggestion} />
					</Box>,
					document.body,
				)}
		</Box>
	);
};

type SuggestionGroupRowProps = {
	parentTitle: string;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
	parentType?: string;
	suggestions: Suggestion[];
	isGroupSelected: boolean;
	selectedItems: Set<string>;
	rejectedItems: Set<string>;
	parentId: number;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	onToggleSelectItem: (id: string) => void;
	onRejectItem: (id: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	spaceId?: string;
	submittedFeedbackIds: Set<string>;
	onFeedbackSubmitted: (suggestionId: string) => void;
	spaceKey?: string;
};

const SuggestionGroupRow = ({
	parentTitle,
	parentType,
	suggestions,
	isGroupSelected,
	selectedItems,
	rejectedItems,
	spaceId,
	spaceKey,
	parentId,
	toggleSelectGroup,
	onToggleSelectItem,
	onRejectItem,
	onGroupHoverChange,
	submittedFeedbackIds,
	onFeedbackSubmitted,
	onMoveSuggestion,
	onSuggestionHover,
}: SuggestionGroupRowProps) => {
	const [isHoverCardVisible, setIsHoverCardVisible] = useState(false);

	return (
		<Box
			xcss={cx(styles.groupCard, isHoverCardVisible && styles.groupCardHovering)}
			onMouseEnter={isGroupSelected ? () => onGroupHoverChange(parentId, true) : undefined}
			onMouseLeave={isGroupSelected ? () => onGroupHoverChange(parentId, false) : undefined}
			onFocusCapture={isGroupSelected ? () => onGroupHoverChange(parentId, true) : undefined}
			onBlur={
				isGroupSelected
					? (event) => {
							if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
								onGroupHoverChange(parentId, false);
							}
						}
					: undefined
			}
			testId={`review-content-suggestion-group-card-${parentId}`}
		>
			<Flex alignItems="center" xcss={styles.groupRow}>
				<Inline alignBlock="center" shouldWrap={false} grow="fill" space="space.075">
					<Box xcss={styles.groupIconSlot}>
						{parentType === 'page' ? (
							<PageIcon label="" color={token('color.icon.subtle')} />
						) : (
							<FolderOpenIcon label="" color={token('color.icon.subtle')} />
						)}
					</Box>
					<Box xcss={styles.groupTitleWrapper}>
						<Text weight="medium" maxLines={1}>
							{parentTitle}
						</Text>
					</Box>
				</Inline>
				<Box padding="space.050">
					<Checkbox isChecked={isGroupSelected} onChange={() => toggleSelectGroup(suggestions)} />
				</Box>
			</Flex>
			<Box xcss={styles.itemsList}>
				{suggestions.map((suggestion) => (
					<SuggestionItemRow
						key={suggestion.id}
						suggestion={suggestion}
						isSelected={selectedItems.has(suggestion.id)}
						isRejected={rejectedItems.has(suggestion.id)}
						spaceId={spaceId}
						hasFeedbackBeenSubmitted={submittedFeedbackIds.has(suggestion.id)}
						onFeedbackSubmitted={onFeedbackSubmitted}
						spaceKey={spaceKey}
						onToggleSelectItem={onToggleSelectItem}
						onRejectItem={onRejectItem}
						onHoverCardVisibilityChange={setIsHoverCardVisible}
						onMoveSuggestion={onMoveSuggestion}
						onSuggestionHover={onSuggestionHover}
					/>
				))}
			</Box>
		</Box>
	);
};

type ModalLeftPanelProps = {
	groupedSuggestionsWithTitles: Map<number, ParentGrouping>;
	contentMetaById: Map<string, { title: string; type: string }>;
	selectedItems: Set<string>;
	rejectedItems: Set<string>;
	toggleSelectGroup: (groupSuggestions: Suggestion[]) => void;
	toggleSelectItem: (itemId: string) => void;
	onRejectItem: (itemId: string) => void;
	onGroupHoverChange: (parentId: number, isHovering: boolean) => void;
	onMoveSuggestion: (suggestionId: string, newParentId: number) => void;
	onSuggestionHover?: (suggestion: Suggestion | null) => void;
	spaceId?: string;
	spaceKey?: string;
};

const ModalLeftPanel = ({
	groupedSuggestionsWithTitles,
	contentMetaById,
	selectedItems,
	rejectedItems,
	toggleSelectGroup,
	toggleSelectItem,
	onRejectItem,
	onGroupHoverChange,
	onMoveSuggestion,
	onSuggestionHover,
	spaceId,
	spaceKey,
}: ModalLeftPanelProps) => {
	const { formatMessage } = useIntl();
	const [submittedFeedbackIds, setSubmittedFeedbackIds] = useState<Set<string>>(new Set());

	const handleFeedbackSubmitted = useCallback((suggestionId: string) => {
		setSubmittedFeedbackIds((prev) => new Set([...prev, suggestionId]));
	}, []);

	return (
		<Stack space="space.250">
			{[...groupedSuggestionsWithTitles.entries()].map(
				([parentId, { parentTitle, suggestions: groupSuggestions }]) => {
					const isGroupSelected = groupSuggestions.some((s) => selectedItems.has(s.id));
					const parentType = contentMetaById.get(String(parentId))?.type;
					return (
						<SuggestionGroupRow
							key={parentId}
							parentTitle={parentTitle || formatMessage(i18n.untitled)}
							parentType={parentType}
							suggestions={groupSuggestions}
							isGroupSelected={isGroupSelected}
							selectedItems={selectedItems}
							rejectedItems={rejectedItems}
							parentId={parentId}
							toggleSelectGroup={toggleSelectGroup}
							onToggleSelectItem={toggleSelectItem}
							onRejectItem={onRejectItem}
							onGroupHoverChange={onGroupHoverChange}
							onMoveSuggestion={onMoveSuggestion}
							onSuggestionHover={onSuggestionHover}
							spaceId={spaceId}
							submittedFeedbackIds={submittedFeedbackIds}
							onFeedbackSubmitted={handleFeedbackSubmitted}
							spaceKey={spaceKey}
						/>
					);
				},
			)}
		</Stack>
	);
};

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

const PreviewContent = ({
	imageUrl,
	title,
	description,
	productIconUrl,
	authorName,
	authorAvatarUrl,
}: {
	imageUrl: string;
	title?: string;
	description?: string;
	productIconUrl?: string;
	authorName?: string;
	authorAvatarUrl?: string;
}) => {
	const [imageError, setImageError] = useState(false);
	const [avatarError, setAvatarError] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false);

	return (
		<Stack space="space.150">
			<Box xcss={styles.contentPreviewImageContainer}>
				{(imageError || !imageLoaded) && (
					<Box xcss={styles.contentPreviewImageFallback}>
						<img src={placeholderIcon} alt="" aria-hidden="true" width="46" height="46" />
					</Box>
				)}
				{!imageError && (
					<img
						src={imageUrl}
						alt=""
						width="100%"
						onLoad={() => setImageLoaded(true)}
						onError={() => setImageError(true)}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						style={{ display: imageLoaded ? 'block' : 'none' }}
					/>
				)}
			</Box>
			{title && (
				<Text weight="medium" maxLines={2}>
					{productIconUrl && (
						<img
							src={productIconUrl}
							alt=""
							width="16"
							height="16"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- inline img alignment can't use xcss
							style={{ verticalAlign: '-3px', marginRight: '6px' }}
						/>
					)}
					{title}
				</Text>
			)}

			{authorName && (
				<Inline space="space.050" alignBlock="center">
					{authorAvatarUrl && !avatarError && (
						<img
							src={authorAvatarUrl}
							alt=""
							width="16"
							height="16"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ borderRadius: '50%' }}
							onError={() => setAvatarError(true)}
						/>
					)}
					<Text size="small" color="color.text.subtlest">
						{authorName}
					</Text>
				</Inline>
			)}
			{description && (
				<Text size="small" color="color.text.subtlest" maxLines={3}>
					{description}
				</Text>
			)}
		</Stack>
	);
};

const ModalRightPanel = ({
	previewSource,
	previewImageUrl,
	previewTitle,
	previewDescription,
	previewProductIconUrl,
	previewAuthorName,
	previewAuthorAvatarUrl,
	onClose,
}: {
	previewSource?: PreviewIconSource;
	previewImageUrl?: string;
	previewTitle?: string;
	previewDescription?: string;
	previewProductIconUrl?: string;
	previewAuthorName?: string;
	previewAuthorAvatarUrl?: string;
	onClose: () => void;
}) => {
	const showGoogleDrive = !previewSource || previewSource === 'google-drive';
	const showLoom = !previewSource || previewSource === 'loom';

	return (
		<Box xcss={styles.rightPanel}>
			<Box xcss={styles.closeButtonWrapper}>
				<IconButton icon={CrossIcon} label="Close" appearance="subtle" onClick={onClose} />
			</Box>
			<Box xcss={styles.contentPreviewContainer}>
				<Box xcss={styles.contentPreviewUnit}>
					<Box xcss={styles.contentPreviewDecorativeBackground}>
						<img src={previewBackground} alt="" aria-hidden="true" width="580" height="520" />
					</Box>
					<Box xcss={styles.contentPreviewIllustration}>
						<Box xcss={styles.contentPreviewDarkBlueBox} />
						<Box xcss={styles.contentPreviewCard}>
							<Box xcss={styles.contentPreviewSparkle}>
								<img src={sparkleIcon} alt="" aria-hidden="true" width="75" height="83" />
							</Box>
							{showGoogleDrive && (
								<Box xcss={styles.contentPreviewIconTopLeft}>
									<Box xcss={styles.contentPreviewGoogleDriveIcon}>
										<img src={googleDriveIcon} alt="" aria-hidden="true" width="32" height="32" />
									</Box>
									<Box xcss={styles.contentPreviewArrowTopLeft}>
										<img src={connectArrowIcon} alt="" aria-hidden="true" width="61" height="36" />
									</Box>
								</Box>
							)}
							{showLoom && (
								<Box xcss={styles.contentPreviewIconBottomRight}>
									<Box xcss={styles.contentPreviewLoomIcon}>
										<img src={loomIcon} alt="" aria-hidden="true" width="32" height="32" />
									</Box>
									<img src={connectArrowIcon} alt="" aria-hidden="true" width="61" height="36" />
								</Box>
							)}
							<Box xcss={styles.contentPreviewCardPadding}>
								{previewImageUrl ? (
									<PreviewContent
										key={previewImageUrl}
										imageUrl={previewImageUrl}
										title={previewTitle}
										description={previewDescription}
										productIconUrl={previewProductIconUrl}
										authorName={previewAuthorName}
										authorAvatarUrl={previewAuthorAvatarUrl}
									/>
								) : (
									<ContentPreviewPlaceholder />
								)}
								{previewSource && (
									<Box xcss={styles.contentPreviewProductLabel}>
										<Inline space="space.050" alignBlock="center">
											<img
												src={
													previewProductIconUrl ||
													(previewSource === 'loom' ? loomIcon : googleDriveIcon)
												}
												alt=""
												width="16"
												height="16"
											/>
											<Text size="small" color="color.text.subtlest">
												{PRODUCT_DISPLAY_NAMES[previewSource]}
											</Text>
										</Inline>
									</Box>
								)}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

type ReviewContentSuggestionsModalInnerProps = ReviewContentSuggestionsModalProps & {
	flags: FlagsStateContainer;
};

export const ReviewContentSuggestionsModalInner = ({
	groups,
	spaceKey,
	spaceName: _spaceName,
	spaceId,
	iconPath: _iconPath,
	onClose,
	onSaveSuccess,
	flags,
}: ReviewContentSuggestionsModalInnerProps) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const [suggestionOverrides, setSuggestionOverrides] = useState<Map<string, { parentId: number }>>(
		() => new Map(),
	);

	const allSuggestions = useMemo(
		() =>
			groups
				.flatMap((g) => g.remoteObjects)
				.map((s) => {
					const override = suggestionOverrides.get(s.id);
					return override ? { ...s, ...override } : s;
				}),
		[groups, suggestionOverrides],
	);

	const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set());
	const [rejectedItems, setRejectedItems] = useState<Set<string>>(() => new Set());
	const [_isHoveringGroupList, setIsHoveringGroupList] = useState(false);
	const [_hoveredParentId, setHoveredParentId] = useState<number | null>(null);
	const [hoveredSuggestion, setHoveredSuggestion] = useState<Suggestion | null>(null);
	const [previewParentId, setPreviewParentId] = useState<number | undefined>(groups[0]?.parentId);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const parentIds = useMemo(() => {
		const overriddenIds = [...suggestionOverrides.values()].map((o) => String(o.parentId));
		return [...new Set([...groups.map((g) => String(g.parentId)), ...overriddenIds])];
	}, [groups, suggestionOverrides]);

	const {
		titlesByContentId,
		contentMetaById,
		isLoading: isLoadingTitles,
	} = useContentTitles(parentIds);
	const { submitActions } = useSubmitSuggestionActions({ spaceKey, flags });

	// Fetch creator display names and avatar URLs for all suggestions
	const creatorAccountIds = useMemo(
		() => allSuggestions.map((s) => s.creatorAccountId).filter(Boolean) as string[],
		[allSuggestions],
	);
	const creatorInfoMap = useCreatorInfo(creatorAccountIds);

	const isLoading = isLoadingTitles || isSubmitting;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const openTimestampRef = useRef(Date.now());
	const dismissMethodRef = useRef<'close_button' | 'escape'>('close_button');

	const sourceTypes = useMemo(
		() =>
			Array.from(new Set(allSuggestions.map((s) => getSuggestionProductKeyFromContentUrl(s.url)))),
		[allSuggestions],
	);

	const mountValuesRef = useRef({
		spaceId,
		spaceKey,
		suggestionCount: allSuggestions.length,
		sourceTypes,
		renderedSuggestions: allSuggestions.map((s, rankIndex) => ({
			suggestionId: s.id,
			suggestionUrl: s.url,
			sourceType: getSuggestionProductKeyFromContentUrl(s.url),
			rankIndex,
			suggestedParentId: s.parentId,
		})),
		createAnalyticsEvent,
	});

	useEffect(() => {
		const {
			spaceId: si,
			spaceKey: sk,
			suggestionCount: sc,
			sourceTypes: st,
			renderedSuggestions: rs,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendScreenEvent',
			data: {
				name: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestionCount: sc,
					sourceTypes: st,
				},
			},
		}).fire();
		cae({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'suggestions',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					suggestions: rs,
				},
			},
		}).fire();
	}, []);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				dismissMethodRef.current = 'escape';
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const handleClose = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationModule',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					suggestionCountAtDismiss: allSuggestions.length,
					dismissMethod: dismissMethodRef.current,
					timeInModuleMs: Date.now() - openTimestampRef.current,
				},
			},
		}).fire();
		if (rejectedItems.size > 0) {
			const rejectedSuggestions = allSuggestions.filter((s) => rejectedItems.has(s.id));
			await submitActions(rejectedSuggestions, new Set(), rejectedItems, {
				showFlags: false,
			});
		}
		dismissMethodRef.current = 'close_button';
		onClose();
	};

	const handleAdd = async () => {
		setIsSubmitting(true);
		try {
			const ok = await submitActions(allSuggestions, selectedItems, rejectedItems);
			if (ok) {
				const suggestionAcceptancePayload = (s: Suggestion) => ({
					suggestionId: s.id,
					suggestionUrl: s.url,
					sourceType: getSuggestionProductKeyFromContentUrl(s.url),
					targetFolderId: s.parentId,
					folderWasRecommended: true,
				});
				const acceptedSuggestions = allSuggestions
					.filter((s) => selectedItems.has(s.id))
					.map(suggestionAcceptancePayload);
				const dismissedSuggestions = allSuggestions
					.filter((s) => !selectedItems.has(s.id))
					.map(suggestionAcceptancePayload);
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'accepted',
						actionSubject: 'suggestions',
						source: 'containerNavigation',
						attributes: {
							spaceId,
							spaceKey,
							count: acceptedSuggestions.length,
							acceptedSuggestions,
							dismissedSuggestions,
						},
					},
				}).fire();
				await onSaveSuccess();
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleMoveSuggestion = useCallback((suggestionId: string, newParentId: number) => {
		setSuggestionOverrides((prev) => new Map(prev).set(suggestionId, { parentId: newParentId }));
		setPreviewParentId(newParentId);
	}, []);

	const groupedSuggestionsWithTitles = useMemo(() => {
		const originalGroups = new Map<number, ParentGrouping>();
		const newGroups = new Map<number, ParentGrouping>();
		const originalParentIds = new Set(groups.map((g) => g.parentId));

		allSuggestions.forEach((suggestion) => {
			const pid = suggestion.parentId;
			const isNewGroup = !originalParentIds.has(pid);
			const target = isNewGroup ? newGroups : originalGroups;
			const existing = target.get(pid);
			if (existing) {
				target.set(pid, { ...existing, suggestions: [...existing.suggestions, suggestion] });
			} else {
				target.set(pid, {
					parentTitle: titlesByContentId.get(String(pid)) ?? '',
					suggestions: [suggestion],
				});
			}
		});

		return new Map([...originalGroups.entries(), ...newGroups.entries()]);
	}, [allSuggestions, titlesByContentId, groups]);

	useEffect(() => {
		if (previewParentId === undefined || groups.length === 0) {
			setPreviewParentId(groups[0]?.parentId);
			return;
		}

		if (!groupedSuggestionsWithTitles.has(previewParentId)) {
			setPreviewParentId(groups[0]?.parentId);
		}
	}, [groups, previewParentId, groupedSuggestionsWithTitles]);

	const handleGroupHoverChange = (parentId: number, isHovering: boolean) => {
		setIsHoveringGroupList(isHovering);
		setHoveredParentId(isHovering ? parentId : null);

		if (isHovering) {
			setPreviewParentId(parentId);
		}
	};

	const toggleSelectItem = (itemId: string) => {
		setSelectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	};

	const toggleRejectItem = (itemId: string) => {
		setRejectedItems((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				// Selecting a reject removes the item from selected
				next.add(itemId);
				setSelectedItems((prevSelected) => {
					const nextSelected = new Set(prevSelected);
					nextSelected.delete(itemId);
					return nextSelected;
				});
			}
			return next;
		});
	};

	const toggleSelectGroup = (groupSuggestions: Suggestion[]) => {
		const allSelected = groupSuggestions.every((s) => selectedItems.has(s.id));
		setSelectedItems((prev) => {
			const next = new Set(prev);
			groupSuggestions.forEach((s) => {
				if (allSelected) {
					next.delete(s.id);
				} else {
					next.add(s.id);
				}
			});
			return next;
		});
	};

	return (
		<Modal onClose={handleClose} width="auto">
			{isLoading ? (
				<Box xcss={styles.loadingOverlay} testId="review-content-suggestions-modal-loading">
					<Spinner
						size="large"
						label={isSubmitting ? formatMessage(i18n.submitting) : formatMessage(i18n.loading)}
					/>
				</Box>
			) : (
				<Flex xcss={styles.layout}>
					<Box xcss={styles.leftPanel}>
						<Box xcss={styles.leftPanelHeader}>
							<Stack space="space.100">
								<Heading size="large" as="h1">
									{formatMessage(i18n.bringTogetherTitle)}
								</Heading>
								<Text color="color.text.subtlest">
									{formatMessage(i18n.bringTogetherDescription)}
								</Text>
							</Stack>
						</Box>
						<Box xcss={styles.groupListScrollArea}>
							<Stack space="space.100">
								<ModalLeftPanel
									groupedSuggestionsWithTitles={groupedSuggestionsWithTitles}
									contentMetaById={contentMetaById}
									selectedItems={selectedItems}
									rejectedItems={rejectedItems}
									toggleSelectGroup={toggleSelectGroup}
									toggleSelectItem={toggleSelectItem}
									onRejectItem={toggleRejectItem}
									onGroupHoverChange={handleGroupHoverChange}
									onMoveSuggestion={handleMoveSuggestion}
									onSuggestionHover={setHoveredSuggestion}
									spaceId={spaceId}
									spaceKey={spaceKey}
								/>
							</Stack>
						</Box>
						<Flex justifyContent="end" xcss={styles.addButtonRow}>
							<Button
								appearance={selectedItems.size > 0 ? 'primary' : 'subtle'}
								onClick={() => void handleAdd()}
								isDisabled={selectedItems.size === 0}
							>
								{selectedItems.size > 0
									? formatMessage(i18n.addItemsToSpace, { count: selectedItems.size })
									: formatMessage(i18n.addToThisSpace)}
							</Button>
						</Flex>
					</Box>
					<ModalRightPanel
						onClose={handleClose}
						previewImageUrl={hoveredSuggestion?.iconUrl}
						previewTitle={hoveredSuggestion?.title}
						previewDescription={hoveredSuggestion?.description}
						previewProductIconUrl={
							hoveredSuggestion
								? getSuggestionIconUrlFromContentUrl(hoveredSuggestion.url)
								: undefined
						}
						previewSource={
							hoveredSuggestion
								? getPreviewSourceFromProductKey(
										getSuggestionProductKeyFromContentUrl(hoveredSuggestion.url),
									)
								: undefined
						}
						previewAuthorName={
							hoveredSuggestion?.creatorAccountId
								? creatorInfoMap.get(hoveredSuggestion.creatorAccountId)?.displayName
								: undefined
						}
						previewAuthorAvatarUrl={
							hoveredSuggestion?.creatorAccountId
								? creatorInfoMap.get(hoveredSuggestion.creatorAccountId)?.avatarUrl
								: undefined
						}
					/>
				</Flex>
			)}
		</Modal>
	);
};

type ReviewContentSuggestionsModalProps = {
	groups: SuggestionGroup[];
	spaceKey?: string;
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
	onClose: () => void;
	onSaveSuccess: () => void | Promise<void>;
};

export const ReviewContentSuggestionsModal = (props: ReviewContentSuggestionsModalProps) => (
	<Subscribe to={[FlagsStateContainer]}>
		{(flags: FlagsStateContainer) => (
			<ReviewContentSuggestionsModalInner {...props} flags={flags} />
		)}
	</Subscribe>
);
